import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../../../components/feature/Navbar';
import { Footer } from '../../../components/feature/Footer';

export default function PremiumWebsitePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[75vh] max-h-[85vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://static.readdy.ai/image/afbb870fba5009b6251518206686e578/2d22fa2dd98cdcab98ef4f1ec23cc841.png"
            alt="Premium website design services"
            className="w-full h-full object-cover object-top"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        {/* Navbar */}
        <div className="absolute top-0 left-0 right-0 z-30">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex items-center justify-between">
              <a href="/" className="flex items-center gap-3 cursor-pointer">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                  <i className="ri-window-line text-2xl text-white"></i>
                </div>
                <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  BusinessWebsitePro
                </span>
              </a>
              
              <nav className="hidden md:flex items-center gap-8">
                <a href="/" className="text-white hover:text-teal-400 transition-colors font-medium cursor-pointer">Home</a>
                <a href="/about" className="text-white hover:text-teal-400 transition-colors font-medium cursor-pointer">About</a>
                <a href="/services" className="text-white hover:text-teal-400 transition-colors font-medium cursor-pointer">Services</a>
                <a href="/portfolio" className="text-white hover:text-teal-400 transition-colors font-medium cursor-pointer">Portfolio</a>
                <a href="/pricing" className="text-white hover:text-teal-400 transition-colors font-medium cursor-pointer">Pricing</a>
                <a href="/blog" className="text-white hover:text-teal-400 transition-colors font-medium cursor-pointer">Blog</a>
                <a href="/contact" className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-6 py-2 rounded-full font-semibold hover:shadow-lg transition-all cursor-pointer whitespace-nowrap">
                  Contact Us
                </a>
              </nav>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="bg-white/[0.02] backdrop-blur-sm border border-white/5 rounded-3xl p-8 sm:p-12 shadow-2xl" style={{ marginLeft: '8rem', maxWidth: '600px' }}>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 drop-shadow-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Premium Website Design Services
            </h1>
            <p className="text-xl text-white/90 mb-8 drop-shadow-lg">
              Advanced custom layouts, product galleries, blog sections, and priority support. Everything you need for a complete online presence.
            </p>
            
            <div className="flex flex-wrap gap-4 justify-end" style={{ marginTop: '150px', marginRight: '75px' }}>
              <a
                href="/booking"
                className="group relative px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-check-line text-xl"></i>
                Book Free Consultation
              </a>
              <a
                href="/portfolio"
                className="group relative px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-xl border-2 border-white/30 hover:bg-white/20 hover:scale-105 transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-eye-line text-xl"></i>
                View Our Work
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Spacer for floating form */}
      <div className="h-80"></div>

      {/* Premium Website Features Deep Dive */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Makes a Premium Website Different
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Premium websites go beyond basic functionality to deliver extraordinary experiences that captivate audiences and drive exceptional business results
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Advanced User Experience Design</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Premium websites feature sophisticated user experience design that anticipates visitor needs and guides them effortlessly through your content. Every interaction is carefully crafted to feel intuitive and delightful, from smooth page transitions to micro-interactions that provide instant feedback.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                We implement advanced UX research methodologies including user journey mapping, heat map analysis, and A/B testing to continuously optimize your website's performance. This data-driven approach ensures your premium website delivers measurable improvements in engagement, conversion rates, and customer satisfaction.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-blue-600 flex-shrink-0"></i>
                  <span className="text-gray-700">Intuitive navigation architecture designed for your specific audience</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-blue-600 flex-shrink-0"></i>
                  <span className="text-gray-700">Strategic placement of calls-to-action based on user behavior patterns</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-blue-600 flex-shrink-0"></i>
                  <span className="text-gray-700">Accessibility compliance ensuring everyone can use your website</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Enterprise-Grade Performance</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Speed is critical for premium websites. Studies show that a one-second delay in page load time can reduce conversions by 7%. Our premium websites are engineered for lightning-fast performance with advanced optimization techniques including code splitting, lazy loading, and intelligent caching strategies.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                We leverage cutting-edge technologies like Content Delivery Networks (CDN), image optimization, and server-side rendering to ensure your website loads instantly anywhere in the world. This technical excellence translates directly to better search engine rankings, higher user engagement, and increased revenue.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-blue-600 flex-shrink-0"></i>
                  <span className="text-gray-700">Sub-second page load times on all devices and connections</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-blue-600 flex-shrink-0"></i>
                  <span className="text-gray-700">Optimized for Core Web Vitals and Google PageSpeed scores</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-blue-600 flex-shrink-0"></i>
                  <span className="text-gray-700">Scalable infrastructure that handles traffic spikes effortlessly</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-white p-12 rounded-2xl border border-blue-100">
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Strategic Content & SEO Excellence</h3>
            <p className="text-gray-600 leading-relaxed mb-6">
              Premium websites combine compelling content with advanced SEO strategies to dominate search results. We don't just optimize for Google – we optimize for the entire digital ecosystem including AI search engines like ChatGPT, Perplexity, and voice assistants that are reshaping how people find information online.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Content Strategy</h4>
                <p className="text-gray-600 leading-relaxed">
                  Professional copywriting that speaks to your audience's needs while incorporating strategic keywords naturally. Every word serves a purpose in guiding visitors toward conversion.
                </p>
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Technical SEO</h4>
                <p className="text-gray-600 leading-relaxed">
                  Advanced schema markup, optimized site architecture, and comprehensive meta data ensure search engines understand and rank your content appropriately.
                </p>
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">AI Optimization</h4>
                <p className="text-gray-600 leading-relaxed">
                  Structured data and semantic markup optimized for AI search engines, ensuring your business appears in AI-generated responses and recommendations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Premium Features Included
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need for a world-class website that stands out from the competition
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-palette-line',
                title: 'Custom Design',
                description: 'Unique, brand-focused design tailored to your business identity and target audience'
              },
              {
                icon: 'ri-smartphone-line',
                title: 'Advanced Responsive Design',
                description: 'Flawless experience across all devices with optimized layouts and interactions'
              },
              {
                icon: 'ri-magic-line',
                title: 'Premium Animations',
                description: 'Sophisticated micro-interactions and animations that enhance user engagement'
              },
              {
                icon: 'ri-search-line',
                title: 'Advanced SEO Optimization',
                description: 'Comprehensive SEO strategy with schema markup, meta optimization, and performance tuning'
              },
              {
                icon: 'ri-dashboard-line',
                title: 'Custom CMS Integration',
                description: 'Easy-to-use content management system for effortless updates and maintenance'
              },
              {
                icon: 'ri-line-chart-line',
                title: 'Analytics & Tracking',
                description: 'Advanced analytics setup with conversion tracking and user behavior insights'
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Enterprise Security',
                description: 'SSL certificate, security headers, and protection against common vulnerabilities'
              },
              {
                icon: 'ri-rocket-line',
                title: 'Performance Optimization',
                description: 'Lightning-fast load times with CDN, image optimization, and code splitting'
              },
              {
                icon: 'ri-customer-service-line',
                title: 'Priority Support',
                description: '6 months of priority support with dedicated account manager'
              },
              {
                icon: 'ri-mail-line',
                title: 'Email Integration',
                description: 'Professional email setup with custom domain and marketing automation'
              },
              {
                icon: 'ri-share-line',
                title: 'Social Media Integration',
                description: 'Seamless integration with all major social media platforms'
              },
              {
                icon: 'ri-file-text-line',
                title: 'Content Strategy',
                description: 'Professional copywriting and content strategy to engage your audience'
              }
            ].map((feature, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-3xl text-blue-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Premium Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Why Choose Premium Website Design?
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <i className="ri-trophy-line text-2xl text-blue-600"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Stand Out from Competition
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      A premium website sets you apart with unique design elements and superior user experience that competitors can't match.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <i className="ri-user-heart-line text-2xl text-blue-600"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Build Trust & Credibility
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      Professional design and smooth functionality create lasting impressions that build customer trust and brand authority.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <i className="ri-arrow-up-line text-2xl text-blue-600"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Increase Conversions
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      Strategic design and optimized user flows guide visitors toward taking action, resulting in higher conversion rates.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <i className="ri-global-line text-2xl text-blue-600"></i>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Scale Your Business
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      Built with growth in mind, premium websites provide the foundation for expanding your online presence globally.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/6ec2f073cb793233bacabdf95f483582.jpg"
                alt="Premium Website Benefits"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Create Your Premium Website?
          </h2>
          <p className="text-xl text-blue-100 mb-10 leading-relaxed">
            Let's discuss your project and create a website that exceeds your expectations
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <i className="ri-message-3-line mr-2 text-xl"></i>
              Contact Us Today
            </Link>
            <Link
              to="/portfolio"
              className="inline-flex items-center px-8 py-4 bg-blue-700 text-white font-semibold rounded-lg hover:bg-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl border-2 border-white/20"
            >
              <i className="ri-eye-line mr-2 text-xl"></i>
              View Our Work
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {/* Form Submission Script */}
      <script
        dangerouslySetInnerHTML={{
          __html: `
            (function() {
              const form = document.getElementById('premium-website-contact-form');
              if (!form) return;

              form.addEventListener('submit', async function(e) {
                e.preventDefault();
                
                const submitBtn = form.querySelector('button[type="submit"]');
                const submitText = submitBtn.querySelector('.submit-text');
                const loadingText = submitBtn.querySelector('.loading-text');
                const messageDiv = document.getElementById('form-message');
                const messageContent = messageDiv.querySelector('div');
                
                // Disable button and show loading
                submitBtn.disabled = true;
                submitText.classList.add('hidden');
                loadingText.classList.remove('hidden');
                messageDiv.classList.add('hidden');
                
                try {
                  const formData = new FormData(form);
                  const data = new URLSearchParams(formData);
                  
                  const response = await fetch('FORM_URL_PLACEHOLDER', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: data.toString()
                  });
                  
                  if (response.ok) {
                    messageContent.className = 'p-4 rounded-lg bg-green-50 border border-green-200 text-green-800';
                    messageContent.innerHTML = '<i class="ri-checkbox-circle-line mr-2"></i>Thank you! We\\'ll contact you within 24 hours.';
                    form.reset();
                  } else {
                    throw new Error('Submission failed');
                  }
                } catch (error) {
                  messageContent.className = 'p-4 rounded-lg bg-red-50 border border-red-200 text-red-800';
                  messageContent.innerHTML = '<i class="ri-error-warning-line mr-2"></i>Something went wrong. Please try again.';
                } finally {
                  submitBtn.disabled = false;
                  submitText.classList.remove('hidden');
                  loadingText.classList.add('hidden');
                  messageDiv.classList.remove('hidden');
                }
              });
            })();
          `
        }}
      />
    </div>
  );
}
